export interface SlashCommandSkill {
  name: string;
  title: string;
  description: string;
  userInvocable?: boolean;
}

/** Server-handled command: saves the exact text that follows as a Memory Entry. */
export const REMEMBER_SLASH_COMMAND: SlashCommandSkill = {
  name: 'remember',
  title: 'Remember',
  description: 'Save a standing rule or preference for future chats. Not for tasks or figures.',
};

const LEADING_SLASH_QUERY = /^\/([a-zA-Z0-9_-]*)$/;

function normalize(value: string): string {
  return value.toLowerCase().trim();
}

function compact(value: string): string {
  return normalize(value).replace(/[^a-z0-9]/g, '');
}

function subsequenceDistance(candidate: string, query: string): number | null {
  let candidateIndex = 0;
  let firstMatch = -1;
  let lastMatch = -1;

  for (const queryCharacter of query) {
    const matchIndex = candidate.indexOf(queryCharacter, candidateIndex);
    if (matchIndex === -1) return null;
    if (firstMatch === -1) firstMatch = matchIndex;
    lastMatch = matchIndex;
    candidateIndex = matchIndex + 1;
  }

  return firstMatch + (lastMatch - firstMatch + 1 - query.length);
}

function scoreSkill(skill: SlashCommandSkill, query: string): number | null {
  const name = normalize(skill.name);
  const title = normalize(skill.title);
  const description = normalize(skill.description);
  const compactName = compact(name);
  const compactTitle = compact(title);
  const compactQuery = compact(query);

  if (name === query) return 0;
  if (name.startsWith(query)) return 10 + name.length - query.length;
  if (compactQuery.length === 0) return null;
  if (compactName.startsWith(compactQuery)) return 20 + compactName.length - compactQuery.length;
  if (title.startsWith(query)) return 30 + title.length - query.length;

  const nameOrTitleWords = `${name} ${title}`.split(/[^a-z0-9]+/);
  if (nameOrTitleWords.some((word) => word.startsWith(query))) return 40;
  if (name.includes(query)) return 50 + name.indexOf(query);
  if (title.includes(query)) return 60 + title.indexOf(query);

  const nameDistance = subsequenceDistance(compactName, compactQuery);
  if (nameDistance != null) return 70 + nameDistance;

  const titleDistance = subsequenceDistance(compactTitle, compactQuery);
  if (titleDistance != null) return 90 + titleDistance;

  const descriptionWords = description.split(/[^a-z0-9]+/);
  if (descriptionWords.some((word) => word.startsWith(query))) return 110;
  if (description.includes(query)) return 120 + description.indexOf(query);
  return null;
}

export function getSlashCommandQuery(message: string): string | null {
  const match = LEADING_SLASH_QUERY.exec(message);
  return match ? normalize(match[1]) : null;
}

export function discoverSlashCommands(
  skills: SlashCommandSkill[],
  message: string,
  limit = 6
): SlashCommandSkill[] {
  const query = getSlashCommandQuery(message);
  if (query == null || limit <= 0) return [];

  return skills
    .filter((skill) => skill.userInvocable !== false)
    .map((skill) => ({
      skill,
      score: query.length === 0 ? 0 : scoreSkill(skill, query),
    }))
    .filter((candidate): candidate is { skill: SlashCommandSkill; score: number } => candidate.score != null)
    .sort((left, right) => left.score - right.score || left.skill.name.localeCompare(right.skill.name))
    .slice(0, limit)
    .map(({ skill }) => skill);
}

export function toCanonicalSlashCommand(skill: SlashCommandSkill): string {
  return `/${skill.name}`;
}
